import { defaultStrings, type ChartStrings } from 'tacho-activity-chart';

/**
 * Translation keys for every label the chart can render.
 *
 * The English values live in `TachoActivityChart` in the site translations
 * (`src/assets/i18n/EN.json` and `tacho-activity-chart.tolgee.en.json`). The
 * chart's own English defaults are used when a key is not translated yet.
 */
const TRANSLATION_KEYS: Record<keyof ChartStrings, string> = {
  driver: 'TachoActivityChart.Driver',
  coDriver: 'TachoActivityChart.CoDriver',
  vehicle: 'TachoActivityChart.Vehicle',
  country: 'TachoActivityChart.Country',
  entered: 'TachoActivityChart.Entered',
  left: 'TachoActivityChart.Left',
  shown: 'TachoActivityChart.Shown',
  noCountry: 'TachoActivityChart.NoCountry',
  noVehicle: 'TachoActivityChart.NoVehicle',
  unknownVehicle: 'TachoActivityChart.UnknownVehicle',
  noDriverPeriod: 'TachoActivityChart.NoDriverPeriod',
  countryCode: 'TachoActivityChart.CountryCode',
  rest: 'TachoActivityChart.Rest',
  availability: 'TachoActivityChart.Availability',
  work: 'TachoActivityChart.Work',
  driving: 'TachoActivityChart.Driving',
  noCard: 'TachoActivityChart.NoCard',
  withDriverCard: 'TachoActivityChart.WithDriverCard',
  withoutDriverCard: 'TachoActivityChart.WithoutDriverCard',
  withTachograph: 'TachoActivityChart.WithTachograph',
  withoutTachograph: 'TachoActivityChart.WithoutTachograph',
  noData: 'TachoActivityChart.NoData',
  outOfScope: 'TachoActivityChart.OutOfScope',
  outOfScopeShort: 'TachoActivityChart.OutOfScopeShort',
  ferry: 'TachoActivityChart.Ferry',
  dstForward: 'TachoActivityChart.DstForward',
  dstBack: 'TachoActivityChart.DstBack',
  manual: 'TachoActivityChart.Manual',
  automatic: 'TachoActivityChart.Automatic',
  cardWithdrawal: 'TachoActivityChart.CardWithdrawal',
  entryType: 'TachoActivityChart.EntryType',
  driverName: 'TachoActivityChart.DriverName',
  cardNumber: 'TachoActivityChart.CardNumber',
  vin: 'TachoActivityChart.Vin',
  duration: 'TachoActivityChart.Duration',
  timeRange: 'TachoActivityChart.TimeRange',
  total: 'TachoActivityChart.Total',
  totals: 'TachoActivityChart.Totals',
  totalsInView: 'TachoActivityChart.TotalsInView',
  odometer: 'TachoActivityChart.Odometer',
  km: 'TachoActivityChart.Km',
  unknownDriver: 'TachoActivityChart.UnknownDriver',
  unknownCard: 'TachoActivityChart.UnknownCard',
  unknownCountry: 'TachoActivityChart.UnknownCountry',
  highlight: 'TachoActivityChart.Highlight',
  marker: 'TachoActivityChart.Marker',
  zoomIn: 'TachoActivityChart.ZoomIn',
  zoomOut: 'TachoActivityChart.ZoomOut',
  resetZoom: 'TachoActivityChart.ResetZoom',
  reset: 'TachoActivityChart.Reset',
  selectRange: 'TachoActivityChart.SelectRange',
  range: 'TachoActivityChart.Range',
  exportSvg: 'TachoActivityChart.ExportSvg',
  exportPng: 'TachoActivityChart.ExportPng',
  noActivity: 'TachoActivityChart.NoActivity',
  chartLabel: 'TachoActivityChart.ChartLabel',
  ferryAt: 'TachoActivityChart.FerryAt',
  outOfScopeRange: 'TachoActivityChart.OutOfScopeRange',
  overview: 'TachoActivityChart.Overview',
  coDriverDisplay: 'TachoActivityChart.CoDriverDisplay',
  coDriverAuto: 'TachoActivityChart.CoDriverAuto',
  coDriverAlways: 'TachoActivityChart.CoDriverAlways',
  coDriverNever: 'TachoActivityChart.CoDriverNever',
};

/**
 * Builds the complete chart label set from the site's translations.
 *
 * `translate` is normally `translateService.instant`. Untranslated keys fall
 * back to the chart's English defaults, so the chart never shows a raw key.
 */
export function buildActivityChartStrings(
  translate: (key: string) => string,
): ChartStrings {
  const strings: ChartStrings = { ...defaultStrings };

  (Object.keys(TRANSLATION_KEYS) as Array<keyof ChartStrings>).forEach((key) => {
    const translationKey = TRANSLATION_KEYS[key];
    const value = translate(translationKey);
    if (value && value !== translationKey) {
      strings[key] = value;
    }
  });

  return strings;
}
